import { useState, useCallback, useMemo, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import { Stack, Box, Typography, Divider, ListItemIcon, ListItemText, ListItemButton } from "@mui/material";
import { Brain } from "@zesty-io/material";
import { useParams, useLocation } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { keyframes } from "@emotion/react";
import { EditRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { ContentInsights } from "./ContentInsights";
import { useGetContentModelQuery, useGetContentModelFieldsQuery, useGetInstanceSettingsQuery } from "../../../../../../../shell/services/instance";
import { AppState } from "../../../../../../../shell/store/types";
import { Error } from "../../../components/Editor/Field/FieldShell";
import { fetchGlobalItem } from "../../../../../../../shell/store/content";
import { ContentItem, ContentModelField, Data, Web } from "../../../../../../../shell/services/types";
import { Meta as MetaType } from "../../../../../../../shell/services/types";
import { SocialMediaPreview } from "./SocialMediaPreview";
import { validateMetaDescription } from "./settings/util";

// Fields
import { MetaImage } from "./settings/MetaImage";
import { CanonicalTag } from "./settings/CanonicalTag";
import { ItemParent } from "./settings/ItemParent";
import { ItemRoute } from "./settings/ItemRoute";
import MetaDescription from "./settings/MetaDescription";
import { MetaKeywords } from "./settings/MetaKeywords";
import { MetaLinkText } from "./settings/MetaLinkText";
import { MetaTitle } from "./settings/MetaTitle";
import { SitemapPriority } from "./settings/SitemapPriority";
import { OGTitle } from "./settings/OGTitle";
import { OGDescription } from "./settings/OGDescription";
import { TCTitle } from "./settings/TCTitle";
import { TCDescription } from "./settings/TCDescription";
import { TCImage } from "./settings/TCImage";
import RedirectsTableContextProvider from "../../../../../../seo/src/views/RedirectsManager/RedirectsTable/RedirectsTableContextProvider";
import IncomingRedirects from "./IncomingRedirects";
import RedirectsDialogContextProvider from "../../../../../../seo/src/app/components/RedirectsDialogProvider";
import { useRegisterRef } from "../../../../../../../engine/useRegisterRef";
const rotateAnimation = keyframes`
	0% {
		background-position: 0% 0%;
	}
	100% {
		background-position: 0% 100%;
	}
`;
const FlowType = {
  AIGenerated: "ai-generated",
  Manual: "manual"
} as const;
const flowButtons = [{
  flowType: FlowType.AIGenerated,
  icon: <Brain sx={{
    fontSize: 32
  }} />,
  primaryTextKey: "content.itemEditMetaAiAssistedPrimary",
  secondaryTextKey: "content.itemEditMetaAiAssistedSecondary"
}, {
  flowType: FlowType.Manual,
  icon: <EditRounded sx={{
    fontSize: 32
  }} />,
  primaryTextKey: "content.itemEditMetaManualPrimary",
  secondaryTextKey: "content.itemEditMetaManualSecondary"
}];
export const MaxLengths: Record<string, number> = {
  metaLinkText: 150,
  metaTitle: 150,
  metaDescription: 160,
  metaKeywords: 255,
  og_title: 150,
  og_description: 160,
  tc_title: 150,
  tc_description: 160
};
export const DYNAMIC_META_FIELD_NAMES = ["og_title", "og_description", "tc_title", "tc_description", "tc_image"];
type Errors = Record<string, Error>;
type MetaProps = {
  isSaving: boolean;
  onUpdateSEOErrors: (errors: Errors) => void;
  errors: Errors;
  errorComponent?: React.ReactNode;
  item: ContentItem;
};
export const Meta = forwardRef(({
  isSaving,
  onUpdateSEOErrors,
  errors,
  errorComponent,
  item
}: MetaProps, ref) => {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const isCreateItemPage = location?.pathname?.split("/")?.pop() === "new";
  const {
    modelZUID,
    itemZUID
  } = useParams<{
    modelZUID: string;
    itemZUID: string;
  }>();
  const {
    data: model,
    isFetching
  } = useGetContentModelQuery(modelZUID, {
    skip: !modelZUID
  });
  const {
    data: fields
  } = useGetContentModelFieldsQuery({
    modelZUID
  });
  const {
    data: instanceSettings,
    isFetching: instanceSettingsFetching
  } = useGetInstanceSettingsQuery();
  const {
    meta,
    data,
    web
  }: {
    meta: MetaType;
    data: Data;
    web: Web;
  } = useSelector((state: AppState) => state.content[isCreateItemPage ? `new:${modelZUID}` : itemZUID]) || {
    meta: {} as MetaType,
    data: {} as Data,
    web: {} as Web
  };
  const homepageItem = useSelector((state: AppState) => Object.values(state.content).find(item => item.web?.pathPart === "zesty_home"));
  const [flowType, setFlowType] = useState<(typeof FlowType)[keyof typeof FlowType]>(null);
  const metaDescriptionButtonRef = useRef(null);
  const metaTitleButtonRef = useRef(null);

  // @ts-expect-error untyped
  const siteName = useMemo(() => dispatch(fetchGlobalItem())?.site_name, []);
  const metaFields = useMemo(() => {
    if (fields?.length) {
      return fields.reduce((accu: Record<string, ContentModelField>, curr: ContentModelField) => {
        if (!curr.deletedAt && DYNAMIC_META_FIELD_NAMES.includes(curr.name.toLowerCase())) {
          accu[curr.name] = curr;
        }
        return accu;
      }, {});
    }
    return {};
  }, [fields]);
  const REQUIRED_FIELDS = useMemo(() => {
    const fields = ["metaTitle", "parentZUID", "pathPart"];
    return fields;
  }, [model]);
  const isHomepage = useMemo(() => {
    if (!homepageItem) return false;
    const homepageZUIDs = Object.values(homepageItem.siblings ?? []);
    return homepageZUIDs.includes(itemZUID);
  }, [homepageItem, itemZUID]);
  const handleOnChange = useCallback(
  // Most callers pass a string, but two `.js` ones the compiler cannot see pass a
  // number: `settings/CanonicalTag.js` (`canonicalTagMode`) and
  // `settings/SitemapPriority.js` (`sitemapPriority`).
  (value: string | number, name: string) => {
    if (!name) {
      throw new Error("Input is missing name attribute");
    }
    const currentErrors = cloneDeep(errors);
    if (REQUIRED_FIELDS.includes(name)) {
      currentErrors[name] = {
        ...currentErrors?.[name],
        MISSING_REQUIRED: !value
      };
    }

    // Neither numeric caller's `name` is a `MaxLengths` key, so this block was
    // already skipped for them; the guard only tells the compiler that.
    if (MaxLengths[name] && typeof value === "string") {
      currentErrors[name] = {
        ...currentErrors?.[name],
        EXCEEDING_MAXLENGTH: value?.length > MaxLengths[name] ? value?.length - MaxLengths[name] : 0
      };
    }
    if (DYNAMIC_META_FIELD_NAMES.includes(name) && name in metaFields) {
      const isRequired = metaFields[name].required;
      currentErrors[name] = {
        ...currentErrors[name],
        MISSING_REQUIRED: isRequired ? !value : false
      };
    }

    // Same as above: neither numeric caller passes `metaDescription`, and
    // `validateMetaDescription` calls `String.prototype.indexOf` on its argument.
    if (name === "metaDescription" && typeof value === "string") {
      const metaDescriptionError = validateMetaDescription(value);
      currentErrors.metaDescription = {
        ...currentErrors.metaDescription,
        CUSTOM_ERROR: !!metaDescriptionError ? metaDescriptionError : ""
      };
    }
    onUpdateSEOErrors(currentErrors);
    dispatch({
      // The og_image is stored as an ordinary field item and not a SEO field item
      type: [...DYNAMIC_META_FIELD_NAMES, "og_image"].includes(name) ? "SET_ITEM_DATA" : "SET_ITEM_WEB",
      itemZUID: meta?.ZUID,
      key: name,
      value: value
    });
  }, [meta?.ZUID, errors]);
  useImperativeHandle(ref, () => {
    return {
      validateMetaFields() {
        const currentErrors = cloneDeep(errors);
        REQUIRED_FIELDS.forEach(fieldName => {
          // @ts-expect-error
          const value = web[fieldName];
          currentErrors[fieldName] = {
            ...currentErrors?.[fieldName],
            MISSING_REQUIRED: !value
          };
        });
        Object.keys(MaxLengths).forEach(fieldName => {
          const value = DYNAMIC_META_FIELD_NAMES.includes(fieldName) ? data[fieldName] :
          // @ts-expect-error
          web[fieldName];
          currentErrors[fieldName] = {
            ...currentErrors?.[fieldName],
            EXCEEDING_MAXLENGTH: value?.length > MaxLengths[fieldName] ? value?.length - MaxLengths[fieldName] : 0
          };
        });
        Object.entries(metaFields).forEach(([name, settings]) => {
          const isRequired = settings.required;
          const value = data[name] as string;
          currentErrors[name] = {
            ...currentErrors?.[name],
            MISSING_REQUIRED: isRequired ? !value : false
          };
        });

        // Validate meta description value
        const metaDescriptionError = validateMetaDescription(web.metaDescription || "");
        currentErrors.metaDescription = {
          ...currentErrors.metaDescription,
          CUSTOM_ERROR: !!metaDescriptionError ? metaDescriptionError : ""
        };

        // No need to validate pathPart for datasets, blocks and homepage
        if (model?.type === "dataset" || model?.type === "block" || isHomepage) {
          delete currentErrors.pathPart;
          delete currentErrors.parentZUID;
        }
        setTimeout(() => {
          // Makes sure that the user sees the error blurbs on each
          // field when in the create item page
          if (isCreateItemPage) {
            setFlowType(FlowType.Manual);
          }
          onUpdateSEOErrors(currentErrors);
        });
        return Object.values(currentErrors)?.map(error => {
          return Object.values(error) ?? [];
        })?.flat().some(error => !!error);
      },
      triggerAIGeneratedFlow() {
        setFlowType(FlowType.AIGenerated);
      }
    };
  }, [errors, web, model, metaFields, data, isHomepage]);
  useEffect(() => {
    if (isSaving) {
      onUpdateSEOErrors({});
      return;
    }
  }, [isSaving]);
  useEffect(() => {
    if (!isCreateItemPage) return;
    if (flowType === FlowType.AIGenerated) {
      // Immediately clear out the existing values for the meta title and description
      dispatch({
        type: "SET_ITEM_WEB",
        itemZUID: meta?.ZUID,
        key: "metaTitle",
        value: ""
      });
      dispatch({
        type: "SET_ITEM_WEB",
        itemZUID: meta?.ZUID,
        key: "metaDescription",
        value: ""
      });

      // Then trigger the AI assistant popup
      metaTitleButtonRef.current?.triggerAIButton?.();
    }
  }, [flowType, isCreateItemPage, meta?.ZUID]);
  const handleMetaTitle = useMemo(() => ({
    setValue: (val: string) => {
      const el = document.getElementById("metaTitle");
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center"
        });
      }
      const inputEl = el.querySelector('input, textarea, [contenteditable="true"]') as HTMLElement | null;
      if (inputEl && typeof inputEl.focus === "function") {
        inputEl.focus();
      }
      handleOnChange(val, "metaTitle");
    }
  }), []);
  const handleMetaDescription = useMemo(() => ({
    setValue: (val: string) => {
      const el = document.getElementById("metaDescription");
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center"
        });
      }
      const inputEl = el.querySelector('input, textarea, [contenteditable="true"]') as HTMLElement | null;
      if (inputEl && typeof inputEl.focus === "function") {
        inputEl.focus();
      }
      handleOnChange(val, "metaDescription");
    }
  }), []);
  useRegisterRef("meta-title", handleMetaTitle, item);
  useRegisterRef("meta-description", handleMetaDescription, item);
  if (isFetching) return null;
  if (isCreateItemPage && flowType === null && model?.type !== "block") {
    return <>
          <Box sx={{
        mt: 2.5,
        mb: 5,
        borderRadius: 2,
        p: 0.25,
        background: "linear-gradient(0deg, rgba(255,93,10,1) 0%, rgba(18,183,106,1) 25%, rgba(11,165,236,1) 50%, rgba(238,70,188,1) 75%, rgba(105,56,239,1) 100%)",
        animation: `${rotateAnimation} 1.5s linear alternate infinite`,
        backgroundSize: "300% 300%"
      }}>
            <Stack gap={3} p={3} bgcolor="background.paper" borderRadius={1.5}>
              <Box>
                <Typography variant="h5" fontWeight={600} mb={1} color="text.primary">
                  {t("content.itemEditMetaAiPromptTitle")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("content.itemEditMetaAiPromptDescriptionPrefix")}{" "}
                  {model?.type === "dataset" ? t("content.itemEditMetaAiPromptDatasetSuffix") : t("content.itemEditMetaAiPromptSeoSuffix")}
                </Typography>
              </Box>
              {flowButtons.map(data => <ListItemButton data-cy={data.flowType === FlowType.AIGenerated ? "AIAssistedMetaFlow" : "ManualMetaFlow"} key={data.flowType} onClick={() => setFlowType(data.flowType)} sx={{
            borderRadius: 2,
            border: 1,
            borderColor: "border",
            backgroundColor: "common.white",
            py: 2
          }}>
                  <ListItemIcon sx={{
              minWidth: 48
            }}>{data.icon}</ListItemIcon>
                  <ListItemText primary={<Typography variant="h6" fontWeight={600} color="text.primary">
                        {t(data.primaryTextKey)}
                      </Typography>} disableTypography sx={{
              my: 0
            }} secondary={<Typography variant="body2" sx={{
              mt: 0.5
            }} color="text.primary">
                        {t(data.secondaryTextKey)}
                      </Typography>} />
                </ListItemButton>)}
            </Stack>
          </Box>
          <Box sx={{
        display: "none"
      }}>
            <ItemParent onChange={handleOnChange} />
          </Box>
        </>;
  }
  if (model?.type === "block" && isCreateItemPage) {
    return <Stack gap={3} pb={2} mb={2} sx={{
      borderBottom: "2px solid",
      borderColor: "border"
    }}>
          {!!errorComponent && errorComponent}
          <MetaTitle label={t("content.itemEditVariantTitle")} aiButtonRef={metaTitleButtonRef} value={web.metaTitle} onChange={handleOnChange} error={errors?.metaTitle} onResetFlowType={() => {
        if (flowType === FlowType.AIGenerated) {
          console.log("reset on meta title");
          setFlowType(FlowType.Manual);
        }
      }} onAIMetaTitleInserted={() => {
        // Scroll to and open the meta description ai generator to continue
        // with the AI-assisted flow
        if (flowType === FlowType.AIGenerated) {
          metaDescriptionButtonRef.current?.triggerAIButton?.();
        }
      }} />
        </Stack>;
  }
  return <Box display="grid" gridTemplateColumns={!isCreateItemPage ? "1fr minmax(auto, 40%)" : "1fr"} gap={4} pt={2.5} mb={isCreateItemPage ? 4 : 0} px={isCreateItemPage ? 0 : 4} color="text.primary" sx={{
    scrollbarWidth: "none",
    overflowY: "auto"
  }}>
        <Stack flex={1} gap={4} minWidth={640}>
          <Stack gap={3}>
            {!!errorComponent && errorComponent}
            <Box>
              <Typography variant="h5" fontWeight={700} mb={0.5}>
                {t("content.itemEditMetaSeoOpenGraphSettings")}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {t("content.itemEditMetaSeoOpenGraphDescription")}
              </Typography>
            </Box>
            <MetaTitle aiButtonRef={metaTitleButtonRef} value={web.metaTitle} onChange={handleOnChange} error={errors?.metaTitle} onResetFlowType={() => {
          if (flowType === FlowType.AIGenerated) {
            console.log("reset on meta title");
            setFlowType(FlowType.Manual);
          }
        }} onAIMetaTitleInserted={() => {
          // Scroll to and open the meta description ai generator to continue
          // with the AI-assisted flow
          if (flowType === FlowType.AIGenerated) {
            metaDescriptionButtonRef.current?.triggerAIButton?.();
          }
        }} />
            <MetaDescription aiButtonRef={metaDescriptionButtonRef} value={web.metaDescription} onChange={handleOnChange} error={errors?.metaDescription} onResetFlowType={() => {
          if (flowType === FlowType.AIGenerated) {
            console.log("reset on meta description");
            setFlowType(FlowType.Manual);
          }
        }} isAIAssistedFlow={flowType === FlowType.AIGenerated} required={REQUIRED_FIELDS.includes("metaDescription")} />
            <MetaImage onChange={handleOnChange} skipImageFallback={instanceSettings?.find(setting => setting.key === "disable_seo_preview")?.value === "1"} />
            {"og_title" in metaFields && <OGTitle value={data.og_title as string} onChange={handleOnChange} error={errors?.og_title} field={metaFields.og_title} />}
            {"og_description" in metaFields && <OGDescription value={data.og_description as string} onChange={handleOnChange} error={errors?.og_description} field={metaFields.og_description} />}
            {"tc_title" in metaFields && <TCTitle value={data.tc_title as string} onChange={handleOnChange} error={errors?.tc_title} field={metaFields.tc_title} />}
            {"tc_description" in metaFields && <TCDescription value={data.tc_description as string} onChange={handleOnChange} error={errors?.tc_description} field={metaFields.tc_description} />}
            {"tc_image" in metaFields && <TCImage field={metaFields.tc_image} onChange={handleOnChange} error={errors?.tc_image} value={data.tc_image as string} />}
          </Stack>
          {model?.type !== "dataset" && !isHomepage && <Stack gap={3}>
              <Box>
                <Typography variant="h5" fontWeight={700} mb={0.5}>
                  {t("content.itemEditMetaUrlSettings")}
                </Typography>
                <Typography color="text.secondary">
                  {t("content.itemEditMetaUrlSettingsDescription")}
                </Typography>
              </Box>
              <ItemParent onChange={handleOnChange} />
              <ItemRoute onChange={handleOnChange} error={errors?.pathPart} onUpdateErrors={(name, error) => {
          onUpdateSEOErrors({
            ...errors,
            [name]: {
              ...errors?.[name],
              ...error
            }
          });
        }} />
              <RedirectsDialogContextProvider>
                <RedirectsTableContextProvider>
                  <IncomingRedirects targetZUID={meta?.ZUID} urlPath={web?.path} />
                </RedirectsTableContextProvider>
              </RedirectsDialogContextProvider>
            </Stack>}
          <Stack gap={3} pb={2.5}>
            <Box>
              <Typography variant="h5" fontWeight={700} mb={0.5}>
                {t("content.itemEditMetaAdvancedSettings")}
              </Typography>
              <Typography color="text.secondary">
                {t("content.itemEditMetaAdvancedSettingsDescription")}
              </Typography>
            </Box>
            {model?.type !== "dataset" && <>
                <SitemapPriority
          // @ts-expect-error untyped
          sitemapPriority={web.sitemapPriority} onChange={handleOnChange} />
                {!!web && <CanonicalTag
          // @ts-expect-error untyped
          mode={web.canonicalTagMode} whitelist={web.canonicalQueryParamWhitelist} custom={web.canonicalTagCustomValue} onChange={handleOnChange} />}
              </>}
            <MetaLinkText value={web.metaLinkText} onChange={handleOnChange} error={errors?.metaLinkText} />
            <MetaKeywords value={web.metaKeywords} onChange={handleOnChange} error={errors?.metaKeywords} />
          </Stack>
        </Stack>
        {!isCreateItemPage && <Box position="sticky" top={0} pb={2.5} alignSelf="start" sx={{
      scrollbarWidth: "none",
      overflowY: "auto"
    }}>
            <Box maxWidth={620}>
              {model?.type !== "dataset" && <>
                  <SocialMediaPreview />
                  <Divider sx={{
            my: 1.5
          }} />
                </>}
              <ContentInsights />
            </Box>
          </Box>}
      </Box>;
});
Meta.displayName = "Meta";